import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  ImageOff,
  LoaderCircle,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent, PointerEvent, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/ui/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@shared/components/ui/Dialog';
import { Separator } from '@shared/components/ui/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/Tooltip';
import { cn } from '@shared/components/ui/cn';

import {
  useTrackVaultFiles,
  useVaultFileLink,
  useVaultFileStatus,
} from '../../hooks/use-vault-file-link';

import { ThumbnailStrip } from './ThumbnailStrip';
import { formatAndSize, sentAtTime, type SentImage } from './file-meta';
import {
  canZoomIn,
  clampPan,
  fitScale,
  ORIGIN,
  stepZoom,
  zoomAround,
  zoomScale,
  type Point,
  type Size,
  type ZoomLevel,
} from './zoom';

type ImageLoad = { status: 'ready'; width: number; height: number } | { status: 'error' };

type ZoomShortcut = 'in' | 'out' | 'fit';

interface Gesture {
  pointerId: number;
  x: number;
  y: number;
  pan: Point;
  mode: 'pan' | 'swipe';
}

const PAN_STEP = 64;
const SWIPE_DISTANCE = 48;
const WHEEL_ZOOM_DISTANCE = 50;
const WHEEL_IDLE_MS = 250;
const WHEEL_LINE_PX = 16;
const WHEEL_PAGE_PX = 400;
const OPENING_GESTURE_MS = 500;
const ARABIC_INDIC_ZERO = '\u0660';
const EXTENDED_ARABIC_INDIC_ZERO = '\u06F0';

const ZOOM_SHORTCUT_KEYS = new Map<string, ZoomShortcut>([
  ['+', 'in'],
  ['=', 'in'],
  ['-', 'out'],
  ['0', 'fit'],
  [ARABIC_INDIC_ZERO, 'fit'],
  [EXTENDED_ARABIC_INDIC_ZERO, 'fit'],
]);

const ZOOM_SHORTCUT_CODES = new Map<string, ZoomShortcut>([
  ['Equal', 'in'],
  ['NumpadAdd', 'in'],
  ['Minus', 'out'],
  ['NumpadSubtract', 'out'],
  ['Digit0', 'fit'],
  ['Numpad0', 'fit'],
]);

function zoomShortcut(event: { key: string; code: string }): ZoomShortcut | undefined {
  return ZOOM_SHORTCUT_KEYS.get(event.key) ?? ZOOM_SHORTCUT_CODES.get(event.code);
}

function wheelPixels(event: WheelEvent, delta: number): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * WHEEL_LINE_PX;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * WHEEL_PAGE_PX;
  return delta;
}

function withReady(
  loads: Record<string, ImageLoad>,
  id: string,
  element: HTMLImageElement,
): Record<string, ImageLoad> {
  if (loads[id]?.status === 'ready') return loads;
  return {
    ...loads,
    [id]: { status: 'ready', width: element.naturalWidth, height: element.naturalHeight },
  };
}

export interface ImageViewerProps {
  images: SentImage[];
  open: boolean;
  startIndex: number;
  sentAt: number;
  returnFocusRef: RefObject<HTMLElement | null>;
  onOpenChange: (open: boolean) => void;
}

export function ImageViewer({
  images,
  open,
  startIndex,
  sentAt,
  returnFocusRef,
  onOpenChange,
}: ImageViewerProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const isRtl = i18n.dir() === 'rtl';
  const total = images.length;
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(startIndex, 0), Math.max(total - 1, 0)),
  );
  const [zoomState, setZoom] = useState<ZoomLevel>('fit');
  const [pan, setPan] = useState<Point>(ORIGIN);
  const [dragging, setDragging] = useState(false);
  const [loads, setLoads] = useState<Record<string, ImageLoad>>({});
  const [attempts, setAttempts] = useState<Record<string, number>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const wheelRef = useRef({ delta: 0, time: 0 });
  const wheelHandlerRef = useRef<(event: WheelEvent) => void>(() => undefined);
  const resizeHandlerRef = useRef<() => void>(() => undefined);
  const openedAtRef = useRef<number | null>(null);
  const focusGuardArmedRef = useRef(false);
  const pendingFocusRef = useRef<RefObject<HTMLButtonElement | null> | null>(null);

  const image = images[index];
  const imageId = image?.id;
  const link = useVaultFileLink(image?.vaultFileId);
  const isRemoved = link?.removed === true;
  const previousRemoved = useVaultFileStatus(images[index - 1]?.vaultFileId) === 'removed';
  const nextRemoved = useVaultFileStatus(images[index + 1]?.vaultFileId) === 'removed';
  useTrackVaultFiles(open ? images.map((item) => item.vaultFileId) : []);
  const load = imageId === undefined ? undefined : loads[imageId];
  const loadStatus = load?.status ?? 'loading';
  const natural: Size | null =
    !isRemoved && load?.status === 'ready' && load.width > 0 && load.height > 0
      ? { width: load.width, height: load.height }
      : null;
  const attempt = imageId === undefined ? 0 : (attempts[imageId] ?? 0);
  const zoom: ZoomLevel = isRemoved ? 'fit' : zoomState;
  const zoomed = zoom !== 'fit';
  const previousUrl = previousRemoved ? undefined : images[index - 1]?.url;
  const nextUrl = nextRemoved ? undefined : images[index + 1]?.url;

  const attachStage = useCallback((node: HTMLDivElement | null) => {
    stageRef.current = node;
    if (!node) return;
    const listener = (event: WheelEvent) => {
      wheelHandlerRef.current(event);
    };
    const observer = new ResizeObserver(() => {
      resizeHandlerRef.current();
    });
    node.addEventListener('wheel', listener, { passive: false });
    observer.observe(node);
    return () => {
      node.removeEventListener('wheel', listener);
      observer.disconnect();
      stageRef.current = null;
    };
  }, []);

  const attachImage = useCallback(
    (element: HTMLImageElement | null) => {
      if (imageId === undefined || !element?.complete || element.naturalWidth === 0) return;
      setLoads((current) => withReady(current, imageId, element));
    },
    [imageId],
  );

  useLayoutEffect(() => {
    wheelHandlerRef.current = handleWheel;
    resizeHandlerRef.current = handleStageResize;
  });

  useEffect(() => {
    openedAtRef.current = open ? performance.now() : null;
  }, [open]);

  useLayoutEffect(() => {
    if (!focusGuardArmedRef.current) {
      focusGuardArmedRef.current = true;
      return;
    }
    const content = contentRef.current;
    const pending = pendingFocusRef.current?.current;
    pendingFocusRef.current = null;
    if (!open || !content) return;
    if (pending && !pending.disabled) {
      pending.focus();
      return;
    }
    const active = document.activeElement;
    const lost =
      active === null ||
      active === document.body ||
      (active instanceof HTMLButtonElement && active.disabled && content.contains(active));
    if (lost) (isRemoved ? (closeRef.current ?? content) : content).focus();
  }, [open, index, zoom, loadStatus, isRemoved]);

  useEffect(() => {
    for (const url of [previousUrl, nextUrl]) {
      if (url) {
        const preload = new Image();
        preload.src = url;
      }
    }
  }, [previousUrl, nextUrl]);

  if (!image) return null;
  const current = image;

  function stageSize(): Size {
    return {
      width: stageRef.current?.clientWidth ?? 0,
      height: stageRef.current?.clientHeight ?? 0,
    };
  }

  function currentFit(): number {
    return natural ? fitScale(natural, stageSize()) : 1;
  }

  function resetZoom() {
    setZoom('fit');
    setPan(ORIGIN);
  }

  function zoomBy(direction: 1 | -1, anchor: Point = ORIGIN) {
    if (!natural) return;
    const fit = currentFit();
    const target = stepZoom(zoom, fit, direction);
    if (target === zoom) return;
    if (target === 'fit') {
      resetZoom();
      return;
    }
    const scale = zoomScale(target, fit);
    setZoom(target);
    setPan(
      clampPan(zoomAround(pan, anchor, zoomScale(zoom, fit), scale), natural, scale, stageSize()),
    );
  }

  function panBy(dx: number, dy: number) {
    if (!natural || !zoomed) return;
    setPan(
      clampPan(
        { x: pan.x + dx, y: pan.y + dy },
        natural,
        zoomScale(zoom, currentFit()),
        stageSize(),
      ),
    );
  }

  function handleStageResize() {
    if (!natural || zoom === 'fit') return;
    const stage = stageSize();
    if (zoom <= fitScale(natural, stage)) {
      resetZoom();
      return;
    }
    setPan((current) => clampPan(current, natural, zoom, stage));
  }

  function isOpeningGesture() {
    const openedAt = openedAtRef.current;
    return openedAt !== null && performance.now() - openedAt < OPENING_GESTURE_MS;
  }

  function isOpeningDoubleClick(event: MouseEvent) {
    return event.detail > 1 && isOpeningGesture();
  }

  function goTo(target: number, viaPointer = false) {
    if (target < 0 || target >= total || target === index) return;
    if (!viaPointer) {
      const active = document.activeElement;
      if (target === 0 && active === previousRef.current) pendingFocusRef.current = nextRef;
      if (target === total - 1 && active === nextRef.current) {
        pendingFocusRef.current = previousRef;
      }
    }
    setIndex(target);
    resetZoom();
  }

  function retry(id: string) {
    setLoads((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => key !== id)),
    );
    setAttempts((current) => ({ ...current, [id]: (current[id] ?? 0) + 1 }));
  }

  function handleWheel(event: WheelEvent) {
    const stage = stageRef.current;
    if (!stage) return;
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (!natural) return;
      const wheel = wheelRef.current;
      if (event.timeStamp - wheel.time > WHEEL_IDLE_MS) wheel.delta = 0;
      wheel.time = event.timeStamp;
      wheel.delta += wheelPixels(event, event.deltaY);
      if (Math.abs(wheel.delta) < WHEEL_ZOOM_DISTANCE) return;
      const direction = wheel.delta < 0 ? 1 : -1;
      wheel.delta = 0;
      const rect = stage.getBoundingClientRect();
      zoomBy(direction, {
        x: event.clientX - rect.left - rect.width / 2,
        y: event.clientY - rect.top - rect.height / 2,
      });
      return;
    }
    if (!zoomed) return;
    event.preventDefault();
    panBy(-wheelPixels(event, event.deltaX), -wheelPixels(event, event.deltaY));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      return;
    }
    if (
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.nativeEvent.isComposing
    ) {
      return;
    }
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        const towardsLeft = event.key === 'ArrowLeft';
        const forward = towardsLeft === isRtl;
        if (zoomed) panBy(towardsLeft ? PAN_STEP : -PAN_STEP, 0);
        else goTo(index + (forward ? 1 : -1));
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
        if (!zoomed) return;
        panBy(0, event.key === 'ArrowUp' ? PAN_STEP : -PAN_STEP);
        break;
      case 'Home':
        goTo(0);
        break;
      case 'End':
        goTo(total - 1);
        break;
      default: {
        const shortcut = zoomShortcut(event);
        if (shortcut === undefined) return;
        if (shortcut === 'fit') resetZoom();
        else zoomBy(shortcut === 'in' ? 1 : -1);
      }
    }
    event.preventDefault();
  }

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;
    const start = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, pan };
    if (zoomed) {
      event.currentTarget.setPointerCapture(event.pointerId);
      gestureRef.current = { ...start, mode: 'pan' };
      setDragging(true);
    } else if (event.pointerType === 'touch' && total > 1) {
      gestureRef.current = { ...start, mode: 'swipe' };
    }
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    const gesture = gestureRef.current;
    if (gesture?.mode !== 'pan' || gesture.pointerId !== event.pointerId || !natural) return;
    setPan(
      clampPan(
        {
          x: gesture.pan.x + event.clientX - gesture.x,
          y: gesture.pan.y + event.clientY - gesture.y,
        },
        natural,
        zoomScale(zoom, currentFit()),
        stageSize(),
      ),
    );
  }

  function handlePointerEnd(event: PointerEvent<HTMLDivElement>) {
    const gesture = gestureRef.current;
    if (gesture?.pointerId !== event.pointerId) return;
    gestureRef.current = null;
    setDragging(false);
    if (gesture.mode !== 'swipe' || event.type !== 'pointerup') return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;
    const forward = dx < 0 !== isRtl;
    goTo(index + (forward ? 1 : -1), true);
  }

  const isReady = loadStatus === 'ready';
  const isError = loadStatus === 'error';
  const zoomControlsEnabled = natural !== null;
  const downloadUrl = isError || isRemoved ? undefined : current.url;
  const vaultDownload = !current.url && link?.canDownload ? link.download : undefined;
  const zoomedSize =
    typeof zoom === 'number' && natural
      ? { width: natural.width * zoom, height: natural.height * zoom }
      : null;
  const zoomLabel =
    zoom === 'fit'
      ? t('imageViewer.zoomFit', 'Fit')
      : t('imageViewer.zoomLevel', '{{value}}%', { value: Math.round(zoom * 100) });
  const meta = `${formatAndSize(current.name, current.size, t, i18n.language)} · ${t(
    'imageViewer.sentByYouAt',
    'Sent by you at {{time}}',
    { time: sentAtTime(sentAt, i18n.language) },
  )}`;
  const hint = zoomed
    ? t('imageViewer.hintZoomed', 'Arrow keys to move · 0 to fit · Esc to close')
    : total > 1
      ? t('imageViewer.hint', '← → to switch · Esc to close')
      : t('imageViewer.hintSingle', 'Esc to close');

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          ref={contentRef}
          showClose={false}
          overlayClassName="bg-neutral-950/60"
          aria-modal="true"
          aria-labelledby={undefined}
          aria-label={t('imageViewer.label', 'Image {{n}} of {{total}}, {{name}}', {
            n: index + 1,
            total,
            name: current.name,
          })}
          onEscapeKeyDown={(event) => {
            event.stopPropagation();
          }}
          onPointerDownOutside={(event) => {
            if (isOpeningGesture()) event.preventDefault();
          }}
          onMouseDownCapture={(event) => {
            if (isOpeningDoubleClick(event)) event.preventDefault();
          }}
          onClickCapture={(event) => {
            if (!isOpeningDoubleClick(event)) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            closeRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocusRef.current?.focus();
          }}
          onKeyDown={handleKeyDown}
          className="flex h-[min(920px,calc(100vh-48px))] w-[min(1152px,calc(100vw-48px))] max-w-none flex-col gap-4 overflow-hidden rounded-xl p-6 outline-none sm:rounded-xl"
        >
          <div className="flex shrink-0 items-start gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <DialogTitle
                dir="auto"
                title={current.name}
                className="w-fit max-w-full truncate text-xl leading-6 font-semibold tracking-normal"
              >
                {current.name}
              </DialogTitle>
              <DialogDescription className="truncate">
                {meta}
                {isRemoved && (
                  <span className="sr-only">
                    {` · ${t('imageViewer.removedTitle', 'This image is no longer in Vault')}`}
                  </span>
                )}
              </DialogDescription>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <ToolbarButton
                label={t('imageViewer.zoomOut', 'Zoom out')}
                disabled={!zoomControlsEnabled || !zoomed}
                onClick={() => {
                  zoomBy(-1);
                }}
              >
                <ZoomOut aria-hidden="true" />
              </ToolbarButton>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!zoomControlsEnabled}
                    onClick={resetZoom}
                    className="rounded-lg text-neutral-700 dark:text-neutral-300"
                  >
                    {zoomLabel}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  {t('imageViewer.resetZoom', 'Fit to screen')}
                </TooltipContent>
              </Tooltip>
              <ToolbarButton
                label={t('imageViewer.zoomIn', 'Zoom in')}
                disabled={!zoomControlsEnabled || !canZoomIn(zoom)}
                onClick={() => {
                  zoomBy(1);
                }}
              >
                <ZoomIn aria-hidden="true" />
              </ToolbarButton>
              <Separator orientation="vertical" className="mx-1 data-[orientation=vertical]:h-5" />
              {downloadUrl ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild variant="ghost" size="icon-sm" className="rounded-lg">
                      <a
                        href={downloadUrl}
                        download={current.name}
                        aria-label={t('imageViewer.download', 'Download')}
                      >
                        <Download aria-hidden="true" />
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={4}>
                    {t('imageViewer.download', 'Download')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <ToolbarButton
                  label={t('imageViewer.download', 'Download')}
                  disabled={!vaultDownload}
                  onClick={vaultDownload}
                >
                  <Download aria-hidden="true" />
                </ToolbarButton>
              )}
              {link && (
                <ToolbarButton
                  label={t('imageViewer.openInVault', 'Open in Vault')}
                  disabled={isRemoved}
                  onClick={() => {
                    void link.open({
                      beforeNavigate: () => {
                        onOpenChange(false);
                      },
                    });
                  }}
                >
                  <ExternalLink aria-hidden="true" />
                </ToolbarButton>
              )}
              <DialogClose asChild>
                <Button
                  ref={closeRef}
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('imageViewer.close', 'Close')}
                  className="rounded-lg"
                >
                  <X aria-hidden="true" />
                </Button>
              </DialogClose>
            </div>
          </div>

          <div
            ref={attachStage}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onDoubleClick={zoomed ? resetZoom : undefined}
            className={cn(
              'relative min-h-0 flex-1 touch-none overflow-hidden rounded-lg border bg-muted select-none',
              zoomed && (dragging ? 'cursor-grabbing' : 'cursor-grab'),
            )}
          >
            {current.url && !isError && !isRemoved && (
              <img
                key={`${current.id}:${String(attempt)}`}
                ref={attachImage}
                src={current.url}
                alt={current.name}
                draggable={false}
                onLoad={(event) => {
                  const element = event.currentTarget;
                  setLoads((loaded) => withReady(loaded, current.id, element));
                }}
                onError={() => {
                  setLoads((loaded) => ({ ...loaded, [current.id]: { status: 'error' } }));
                }}
                style={
                  zoomedSize
                    ? {
                        width: zoomedSize.width,
                        height: zoomedSize.height,
                        transform: `translate(${String(pan.x)}px, ${String(pan.y)}px)`,
                      }
                    : undefined
                }
                className={cn(
                  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border bg-background',
                  zoomedSize ? 'max-w-none' : 'max-h-[calc(100%-32px)] max-w-[calc(100%-32px)]',
                  !isReady && 'opacity-60 blur-[3px]',
                  isReady && total > 1 && 'shadow-[0_2px_6px_rgba(0,0,0,0.06)]',
                )}
              />
            )}
            {loadStatus === 'loading' && !isRemoved && (
              <div className="pointer-events-none absolute inset-0 m-auto flex h-fit w-fit items-center gap-2 rounded-full border bg-background px-3 py-2 text-xs font-medium text-foreground">
                <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
                {t('imageViewer.loading', 'Loading full size…')}
              </div>
            )}
            <span role="status" aria-live="polite" className="sr-only">
              {isRemoved
                ? t('imageViewer.removedTitle', 'This image is no longer in Vault')
                : isError
                  ? t('imageViewer.errorTitle', 'Couldn’t load this image')
                  : ''}
            </span>
            {isRemoved ? (
              <StageNotice
                title={t('imageViewer.removedTitle', 'This image is no longer in Vault')}
                description={t(
                  'imageViewer.removedDescription',
                  'It was removed after it was sent. The other images are still available.',
                )}
              />
            ) : (
              isError && (
                <StageNotice
                  title={t('imageViewer.errorTitle', 'Couldn’t load this image')}
                  description={t(
                    'imageViewer.errorDescription',
                    'Check your connection and try again. The other images are still available.',
                  )}
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg has-[>svg]:px-4"
                    onClick={() => {
                      retry(current.id);
                    }}
                  >
                    <RotateCw aria-hidden="true" />
                    {t('imageViewer.retry', 'Try again')}
                  </Button>
                </StageNotice>
              )
            )}
            {total > 1 && (
              <>
                <StageArrow
                  buttonRef={previousRef}
                  edge="start"
                  isRtl={isRtl}
                  label={t('imageViewer.previous', 'Previous image')}
                  tooltip={t('imageViewer.previousShortcut', 'Previous image (←)')}
                  disabled={index === 0}
                  onClick={(event) => {
                    goTo(index - 1, event.detail > 0);
                  }}
                >
                  <ChevronLeft className="rtl:-scale-x-100" aria-hidden="true" />
                </StageArrow>
                <StageArrow
                  buttonRef={nextRef}
                  edge="end"
                  isRtl={isRtl}
                  label={t('imageViewer.next', 'Next image')}
                  tooltip={t('imageViewer.nextShortcut', 'Next image (→)')}
                  disabled={index === total - 1}
                  onClick={(event) => {
                    goTo(index + 1, event.detail > 0);
                  }}
                >
                  <ChevronRight className="rtl:-scale-x-100" aria-hidden="true" />
                </StageArrow>
              </>
            )}
            {zoomed && (
              <p className="pointer-events-none absolute inset-x-0 bottom-[15px] mx-auto w-fit max-w-[calc(100%-32px)] truncate rounded-full bg-neutral-950/80 px-3 py-1.5 text-xs font-medium text-white">
                {t('imageViewer.panHint', 'Drag to move · Double-click or 0 to fit')}
              </p>
            )}
          </div>

          <div className="flex h-16 shrink-0 items-center justify-between gap-4">
            <p aria-live="polite" className="shrink-0 text-sm font-medium md:w-[300px]">
              {total > 1
                ? t('imageViewer.counter', '{{n}} of {{total}}', { n: index + 1, total })
                : ''}
            </p>
            {total > 1 && <ThumbnailStrip images={images} current={index} onSelect={goTo} />}
            <p
              className={cn(
                'w-[300px] text-end text-xs text-muted-foreground',
                total > 1 ? 'hidden shrink-0 lg:block' : 'min-w-0 truncate',
              )}
            >
              {hint}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

interface StageNoticeProps {
  title: string;
  description: string;
  children?: ReactNode;
}

function StageNotice({ title, description, children }: StageNoticeProps) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 overflow-y-auto p-4">
      <div className="grid size-10 shrink-0 place-items-center rounded-lg border bg-background">
        <ImageOff className="size-6" aria-hidden="true" />
      </div>
      <div className="flex w-full max-w-[372px] flex-col items-center gap-4 p-8 text-center">
        <div className="flex flex-col gap-1">
          <p className="text-base font-medium text-primary">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

function ToolbarButton({ label, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          className="rounded-lg"
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={4}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

interface StageArrowProps {
  buttonRef: RefObject<HTMLButtonElement | null>;
  edge: 'start' | 'end';
  isRtl: boolean;
  label: string;
  tooltip: string;
  disabled: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}

function StageArrow({
  buttonRef,
  edge,
  isRtl,
  label,
  tooltip,
  disabled,
  onClick,
  children,
}: StageArrowProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={buttonRef}
          type="button"
          variant="outline"
          size="icon"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 rounded-full hover:border-neutral-300 hover:bg-background',
            edge === 'start' ? 'start-[15px]' : 'end-[15px]',
          )}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side={(edge === 'start') !== isRtl ? 'right' : 'left'} sideOffset={8}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
